/**
 * v1 Refill Settings Dialog Component
 * Modal for managing user auto-refill settings
 */

import { useState, useEffect } from 'react'
import { useUpdateRefillSettings } from '../hooks/useUpdateRefillSettings'
import { User } from '../api'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Alert } from '@/components/ui/alert'
import { RefreshCw, AlertCircle, Info } from 'lucide-react'

interface RefillSettingsDialogProps {
  user: User
  onClose: () => void
}

// Preset interval options
const INTERVAL_OPTIONS = [
  { value: '1-days', label: '1 Day', intervalValue: 1, intervalUnit: 'days' as const },
  { value: '3-days', label: '3 Days', intervalValue: 3, intervalUnit: 'days' as const },
  { value: '5-days', label: '5 Days', intervalValue: 5, intervalUnit: 'days' as const },
  { value: '15-days', label: '15 Days', intervalValue: 15, intervalUnit: 'days' as const },
  { value: '1-weeks', label: '1 Week', intervalValue: 1, intervalUnit: 'weeks' as const },
  { value: '1-months', label: '1 Month', intervalValue: 1, intervalUnit: 'months' as const },
]

export function RefillSettingsDialog({ user, onClose }: RefillSettingsDialogProps) {
  const [autoRefillEnabled, setAutoRefillEnabled] = useState(user.balance?.autoRefillEnabled ?? false)
  const [refillAmount, setRefillAmount] = useState(String(user.balance?.refillAmount || '10000'))
  const [selectedInterval, setSelectedInterval] = useState(() => {
    // Find matching preset or default to 1 month
    const current = user.balance
    if (current?.refillIntervalValue && current?.refillIntervalUnit) {
      const match = INTERVAL_OPTIONS.find(
        opt => opt.intervalValue === current.refillIntervalValue && opt.intervalUnit === current.refillIntervalUnit
      )
      return match?.value || '1-months'
    }
    return '1-months'
  })
  const [error, setError] = useState('')

  const { mutate: updateSettings, isPending } = useUpdateRefillSettings()

  useEffect(() => {
    // Update enabled state when user changes
    setAutoRefillEnabled(user.balance?.autoRefillEnabled ?? false)
  }, [user.balance?.autoRefillEnabled])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    // If disabling, just send disabled flag
    if (!autoRefillEnabled) {
      updateSettings(
        {
          userId: user._id,
          autoRefillEnabled: false,
        },
        {
          onSuccess: () => {
            onClose()
          }
        }
      )
      return
    }

    // Validate amount
    const parsedAmount = parseFloat(refillAmount)
    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      setError('Please enter a valid refill amount')
      return
    }

    // Get selected interval
    const interval = INTERVAL_OPTIONS.find(opt => opt.value === selectedInterval)
    if (!interval) {
      setError('Please select a valid interval')
      return
    }

    updateSettings(
      {
        userId: user._id,
        autoRefillEnabled: true,
        refillAmount: parsedAmount,
        refillIntervalValue: interval.intervalValue,
        refillIntervalUnit: interval.intervalUnit,
      },
      {
        onSuccess: () => {
          onClose()
        }
      }
    )
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5" />
            Auto-Refill Settings
          </DialogTitle>
          <DialogDescription>
            Configure automatic token refill for {user.name || user.email}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* User Info */}
          <div className="rounded-lg bg-muted p-4">
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm font-medium">User</span>
                <span className="text-sm">{user.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm font-medium">Current Balance</span>
                <span className="text-sm font-mono">
                  {(user.tokenCredits || 0).toFixed(2)} tokens
                </span>
              </div>
            </div>
          </div>

          {/* Enable/Disable Switch */}
          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <Label htmlFor="auto-refill" className="text-base">
                Enable Auto-Refill
              </Label>
              <div className="text-sm text-muted-foreground">
                Automatically add tokens when balance runs low
              </div>
            </div>
            <Switch
              id="auto-refill"
              checked={autoRefillEnabled}
              onCheckedChange={setAutoRefillEnabled}
              disabled={isPending}
            />
          </div>

          {/* Refill Configuration (only shown when enabled) */}
          {autoRefillEnabled && (
            <>
              {/* Info Alert */}
              <Alert>
                <Info className="h-4 w-4" />
                <div className="ml-2 text-sm">
                  SAIFChat will automatically add tokens when balance reaches zero,
                  if the interval has elapsed since the last refill.
                </div>
              </Alert>

              {/* Refill Amount */}
              <div className="space-y-2">
                <Label htmlFor="refill-amount">
                  Refill Amount <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="refill-amount"
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={refillAmount}
                  onChange={(e) => setRefillAmount(e.target.value)}
                  placeholder="Enter refill amount (e.g., 10000)"
                  required
                  disabled={isPending}
                />
                <p className="text-xs text-muted-foreground">
                  Number of tokens to add on each refill
                </p>
              </div>

              {/* Interval Selection */}
              <div className="space-y-2">
                <Label htmlFor="interval">
                  Refill Interval <span className="text-destructive">*</span>
                </Label>
                <Select
                  value={selectedInterval}
                  onValueChange={setSelectedInterval}
                  disabled={isPending}
                >
                  <SelectTrigger id="interval">
                    <SelectValue placeholder="Select interval" />
                  </SelectTrigger>
                  <SelectContent>
                    {INTERVAL_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Minimum time between automatic refills
                </p>
              </div>
            </>
          )}

          {/* Error Alert */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <div className="ml-2">{error}</div>
            </Alert>
          )}

          {/* Actions */}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isPending || (autoRefillEnabled && (!refillAmount || parseFloat(refillAmount) <= 0))}
            >
              {isPending ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  Saving...
                </>
              ) : (
                <>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Save Settings
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
